import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Card, Button, Tag, Space, Typography, Input,
  Select, Drawer, Form, Avatar, Badge, Divider,
  Row, Col, Statistic, Timeline, Empty, message, Popconfirm,
  Tabs, Modal, Radio, Progress, Alert, List,
} from 'antd';
import {
  PlusOutlined, PhoneOutlined, MailOutlined,
  EditOutlined, DeleteOutlined,
  SearchOutlined, ReloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  DollarOutlined, EnvironmentOutlined, SaveOutlined,
  LinkOutlined, GlobalOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const OFFICE_API = '/api/v1/house/office/crm';
const HOME_API = '/api/v1/house/home/crm';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

// ── Office leasing pipeline ─────────────────────────────
const getPipeline = (t) => [
  { key: 'new',          label: t('jjcrm.pipeline.new', 'New Inquiry'),    color: 'purple' },
  { key: 'contacted',    label: t('jjcrm.pipeline.contacted', 'Contacted'),    color: 'blue' },
  { key: 'viewing',      label: t('jjcrm.pipeline.viewing', 'Viewing'),    color: 'orange' },
  { key: 'negotiating',  label: t('jjcrm.pipeline.negotiating', 'Negotiating'),    color: 'geekblue' },
  { key: 'closed_won',   label: t('jjcrm.pipeline.closedWon', 'Closed (Won)'),    color: 'green' },
  { key: 'closed_lost',  label: t('jjcrm.pipeline.closedLost', 'Closed (Lost)'),    color: 'red' },
];
const getStageMap = (t) => Object.fromEntries(getPipeline(t).map(s => [s.key, s]));

const getSources = (t) => ({
  manual: { label: t('jjcrm.source.manual', 'Manual'), color: 'default' },
  line:   { label: '💬 LINE', color: '#00b900' },
  whatsapp: { label: '📱 WhatsApp', color: '#25d366' },
  website: { label: t('jjcrm.source.website', 'Website'), color: '#0284c7' },
  referral: { label: t('jjcrm.source.referral', 'Referral'), color: '#722ed1' },
  office_search: { label: t('jjcrm.source.officeSearch', 'Office Search'), color: '#13c2c2' },
});

function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return '-';
  return '฿' + Number(n).toLocaleString();
}

// ── Case Detail Drawer ──────────────────────────────────
function CaseDrawer({ leadId, open, onClose, onChanged, onOpenOffice, apiBase, segment }) {
  const { t } = useTranslation();
  const API = apiBase || OFFICE_API;
  const PIPELINE = getPipeline(t);
  const SOURCES = getSources(t);
  const STAGE_MAP = getStageMap(t);
  const [detail, setDetail] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [closeModal, setCloseModal] = useState(false);
  const [closeForm] = Form.useForm();
  const [linkModal, setLinkModal] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [activityText, setActivityText] = useState('');
  const [negForm] = Form.useForm();
  const [negModal, setNegModal] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    const d = await fetch(`${API}/leads/${leadId}`, { headers: h() }).then(r => r.json());
    setDetail(d);
    form.setFieldsValue({
      name: d.name, company_name: d.company_name, phone: d.phone, email: d.email,
      language_pref: d.language_pref, source: d.source,
      stage: d.stage, lead_score: d.lead_score,
      budget_min: d.budget_min, budget_max: d.budget_max,
      office_type_pref: d.office_type_pref, seats_needed: d.seats_needed,
      employee_count: d.employee_count, area_sqm_min: d.area_sqm_min, area_sqm_max: d.area_sqm_max,
      lease_months: d.lease_months, move_in_timeline: d.move_in_timeline, boi_status: d.boi_status,
      preferred_types: (d.preferred_types || []).join(', '),
      preferred_bedrooms: d.preferred_bedrooms,
      preferred_locations: (d.preferred_locations || []).join(', '),
      notes: d.notes,
    });
  }, [leadId, form, API]);

  useEffect(() => { if (open) { setDetail(null); load(); } }, [open, leadId, load]);

  const handleSave = async () => {
    const vals = await form.validateFields();
    const body = {
      ...vals,
      preferred_locations: vals.preferred_locations ? vals.preferred_locations.split(',').map(s => s.trim()).filter(Boolean) : [],
      preferred_types: vals.preferred_types ? vals.preferred_types.split(',').map(s => s.trim()).filter(Boolean) : [],
      preferred_bedrooms: vals.preferred_bedrooms ? Number(vals.preferred_bedrooms) : null,
      budget_min: vals.budget_min ? Number(vals.budget_min) : null,
      budget_max: vals.budget_max ? Number(vals.budget_max) : null,
      area_sqm_min: vals.area_sqm_min ? Number(vals.area_sqm_min) : null,
      area_sqm_max: vals.area_sqm_max ? Number(vals.area_sqm_max) : null,
    };
    setSaving(true);
    try {
      await fetch(`${API}/leads/${leadId}`, { method: 'PATCH', headers: h(), body: JSON.stringify(body) });
      message.success(t('jjcrm.saved', 'Saved'));
      load(); onChanged();
    } finally { setSaving(false); }
  };

  const linkProperty = async (propertyId) => {
    await fetch(`${API}/leads/${leadId}/properties?property_id=${propertyId}&status=interested`, { method: 'POST', headers: h() });
    message.success(t('jjcrm.addedToCase', 'Added to case'));
    load(); onChanged();
  };
  const unlinkProperty = async (propertyId) => {
    await fetch(`${API}/leads/${leadId}/properties/${propertyId}`, { method: 'DELETE', headers: h() });
    message.success(t('jjcrm.removed', 'Removed'));
    load(); onChanged();
  };

  const addActivity = async (summary, type = 'note') => {
    if (!summary.trim()) return;
    await fetch(`${API}/leads/${leadId}/activities`, { method: 'POST', headers: h(), body: JSON.stringify({ summary, activity_type: type }) });
    setActivityText('');
    load(); onChanged();
  };

  const startNegotiation = async () => {
    const vals = await negForm.validateFields();
    await fetch(`${API}/leads/${leadId}/negotiations`, { method: 'POST', headers: h(), body: JSON.stringify({
      property_id: vals.property_id, asking_price: Number(vals.asking_price), offer_price: vals.offer_price ? Number(vals.offer_price) : null,
    }) });
    message.success(t('jjcrm.negotiationCreated', 'Negotiation created'));
    setNegModal(false);
    load(); onChanged();
  };

  const closeDeal = async () => {
    const vals = await closeForm.validateFields();
    await fetch(`${API}/leads/${leadId}/close`, { method: 'POST', headers: h(), body: JSON.stringify({
      outcome: vals.outcome, won_rent: vals.won_rent ? Number(vals.won_rent) : null,
      won_property_id: vals.won_property_id || null, lost_reason: vals.lost_reason,
    }) });
    message.success(vals.outcome === 'won' ? t('jjcrm.markedWon', 'Marked as won') : t('jjcrm.markedLost', 'Marked as lost'));
    setCloseModal(false);
    load(); onChanged();
  };

  const searchOffice = async (kw) => {
    const q = new URLSearchParams({ limit: 20, sort_by: 'price', sort_order: 'asc' });
    if (kw) q.append('search', kw);
    const listApi = segment === 'home' ? '/api/v1/house/home/list' : '/api/v1/house/office/list';
    const d = await fetch(`${listApi}?${q}`, { headers: h() }).then(r => r.json());
    setLinkResults(d.items || []);
  };
  useEffect(() => {
    if (linkModal) searchOffice(linkSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkModal]);

  if (!open) return null;

  const c = detail || {};
  const matches = c.matches || [];
  const negotiations = c.negotiations || [];
  const activities = c.activities || [];
  const isWon = c.stage === 'closed_won';
  const isLost = c.stage === 'closed_lost';

  return (
    <Drawer
      title={
        <Space>
          <Avatar size={36} style={{ background: isWon ? '#52c41a' : isLost ? '#ff4d4f' : '#1890ff' }}>
            {(c.company_name || c.name || '?')[0]}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 15 }}>{c.company_name || c.name}</Text>
            <br />
            <Space size={4} wrap>
              <Tag color={STAGE_MAP[c.stage]?.color || 'default'} style={{ margin: 0, fontSize: 11 }}>{STAGE_MAP[c.stage]?.label || c.stage}</Tag>
              <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>🔥 {c.lead_score || 0}</Tag>
              {(SOURCES[c.source] || SOURCES.manual) && <Tag style={{ margin: 0, fontSize: 11 }}>{SOURCES[c.source]?.label || c.source}</Tag>}
              {c.boi_status === 'applied' && <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>{t('jjcrm.boiApplied', 'BOI Applied')}</Tag>}
              {c.boi_status === 'approved' && <Tag color="green" style={{ margin: 0, fontSize: 11 }}>{t('jjcrm.boiApproved', 'BOI Approved')}</Tag>}
            </Space>
          </div>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={760}
      extra={
        <Space>
          {!isWon && !isLost && (
            <>
              <Button type="primary" danger icon={<CheckCircleOutlined />} onClick={() => { closeForm.setFieldsValue({ outcome: 'won' }); setCloseModal(true); }}>{t('jjcrm.closeDeal', 'Close Deal')}</Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => { closeForm.setFieldsValue({ outcome: 'lost' }); setCloseModal(true); }}>{t('jjcrm.markLost', 'Mark Lost')}</Button>
            </>
          )}
          <Popconfirm title={t('jjcrm.confirmDeleteCase', 'Delete this case?')} onConfirm={async () => {
            await fetch(`${API}/leads/${leadId}`, { method: 'DELETE', headers: h() });
            message.success(t('jjcrm.deleted', 'Deleted'));
            onClose(); onChanged();
          }} okText={t('common.delete', 'Delete')} cancelText={t('common.cancel', 'Cancel')} okButtonProps={{ danger: true }}>
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>{t('common.save', 'Save')}</Button>
        </Space>
      }
    >
      {!detail ? <Text type="secondary">{t('common.loading', 'Loading...')}</Text> : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
          {/* customer & requirements */}
          <TabPane tab={`📋 ${t('jjcrm.tabCustomerReq', 'Customer & Requirements')}`} key="info">
            {isWon && <Alert type="success" showIcon message={`${t('jjcrm.wonAlert', 'Closed (Won)')}${c.won_rent ? ' — ' + t('jjcrm.monthRent', 'Monthly Rent') + ' ' + fmtMoney(c.won_rent) : ''}`} style={{ marginBottom: 12 }} />}
            {isLost && <Alert type="error" showIcon message={`${t('jjcrm.lostAlert', 'Closed (Lost)')} — ${c.lost_reason || t('jjcrm.noReason', 'No reason')}`} style={{ marginBottom: 12 }} />}
            <Form form={form} layout="vertical" size="small">
              <Row gutter={12}>
                {segment !== 'home' && <Col span={8}><Form.Item name="company_name" label={t('jjcrm.companyName', 'Company Name')}><Input /></Form.Item></Col>}
                <Col span={segment === 'home' ? 16 : 8}><Form.Item name="name" label={t('jjcrm.contact', 'Contact')}><Input /></Form.Item></Col>
                <Col span={8}><Form.Item name="stage" label={t('jjcrm.stage', 'Stage')}>
                  <Select>
                    {PIPELINE.map(s => <Option key={s.key} value={s.key}>{s.label}</Option>)}
                  </Select>
                </Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}><Form.Item name="phone" label={t('jjcrm.phone', 'Phone')}><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
                <Col span={8}><Form.Item name="email" label="Email"><Input prefix={<MailOutlined />} /></Form.Item></Col>
                <Col span={8}><Form.Item name="language_pref" label={t('jjcrm.language', 'Language')}>
                  <Select>
                    <Option value="zh">{t('jjcrm.langZh', 'Chinese')}</Option><Option value="en">English</Option>
                    <Option value="th">ไทย</Option><Option value="ja">{t('jjcrm.langJa', 'Japanese')}</Option><Option value="other">{t('common.other', 'Other')}</Option>
                  </Select>
                </Form.Item></Col>
              </Row>
              <Divider style={{ margin: '8px 0' }}>{segment === 'home' ? t('jjcrm.homeReq', 'Home Requirements') : t('jjcrm.officeReq', 'Office Requirements')}</Divider>
              {segment === 'home' ? (
                <>
                  <Row gutter={12}>
                    <Col span={6}><Form.Item name="preferred_types" label={t('jjcrm.preferredTypes', 'Property Types')}><Input placeholder="condo, house" /></Form.Item></Col>
                    <Col span={6}><Form.Item name="preferred_bedrooms" label={t('jjcrm.preferredBedrooms', 'Bedrooms')}><Input type="number" /></Form.Item></Col>
                    <Col span={6}><Form.Item name="move_in_timeline" label={t('jjcrm.moveInTimeline', 'Move-in Timeline')}>
                      <Select allowClear>
                        <Option value="immediate">{t('jjcrm.timelineImmediate', 'Immediate')}</Option>
                        <Option value="1month">{t('jjcrm.timeline1mo', 'Within 1 month')}</Option>
                        <Option value="3months">{t('jjcrm.timeline3mo', 'Within 3 months')}</Option>
                        <Option value="flexible">{t('jjcrm.timelineFlexible', 'Flexible')}</Option>
                      </Select>
                    </Form.Item></Col>
                    <Col span={6}><Form.Item name="nationality" label={t('jjcrm.nationality', 'Nationality')}><Input /></Form.Item></Col>
                  </Row>
                </>
              ) : (
              <>
              <Row gutter={12}>
                <Col span={6}><Form.Item name="office_type_pref" label={t('jjcrm.officeType', 'Office Type')}>
                  <Select allowClear>
                    <Option value="office">{t('jjcrm.officeTypeOffice', 'Office')}</Option>
                    <Option value="coworking">{t('jjcrm.officeTypeCoworking', 'Coworking')}</Option>
                    <Option value="serviced">{t('jjcrm.officeTypeServiced', 'Serviced Office')}</Option>
                  </Select>
                </Form.Item></Col>
                <Col span={6}><Form.Item name="seats_needed" label={t('jjcrm.seatsNeeded', 'Seats Needed')}><Input type="number" /></Form.Item></Col>
                <Col span={6}><Form.Item name="employee_count" label={t('jjcrm.employeeCount', 'Employees')}><Input type="number" /></Form.Item></Col>
                <Col span={6}><Form.Item name="boi_status" label="BOI">
                  <Select allowClear>
                    <Option value="none">{t('common.none', 'None')}</Option><Option value="applied">{t('jjcrm.boiApplied', 'Applied')}</Option><Option value="approved">{t('jjcrm.boiApproved', 'Approved')}</Option>
                  </Select>
                </Form.Item></Col>
              </Row>
              </>)}
              <Row gutter={12}>
                <Col span={segment === 'home' ? 12 : 6}><Form.Item name="budget_min" label={t('jjcrm.budgetMin', 'Budget Min (THB)')}><Input type="number" /></Form.Item></Col>
                <Col span={segment === 'home' ? 12 : 6}><Form.Item name="budget_max" label={t('jjcrm.budgetMax', 'Budget Max (THB)')}><Input type="number" /></Form.Item></Col>
                {segment !== 'home' && <><Col span={6}><Form.Item name="area_sqm_min" label={t('jjcrm.areaMin', 'Min Area (sqm)')}><Input type="number" /></Form.Item></Col>
                <Col span={6}><Form.Item name="area_sqm_max" label={t('jjcrm.areaMax', 'Max Area (sqm)')}><Input type="number" /></Form.Item></Col></>}
              </Row>
              <Row gutter={12}>
                <Col span={segment === 'home' ? 12 : 8}><Form.Item name="preferred_locations" label={t('jjcrm.preferredLocationsHint', 'Preferred Areas (comma separated)')}><Input placeholder="Pathum Wan, Sukhumvit" /></Form.Item></Col>
                {segment !== 'home' && <Col span={8}><Form.Item name="lease_months" label={t('jjcrm.leaseMonths', 'Lease (months)')}><Input type="number" /></Form.Item></Col>}
                {segment !== 'home' && <Col span={8}><Form.Item name="move_in_timeline" label={t('jjcrm.moveInTimeline', 'Move-in Timeline')}>
                  <Select allowClear>
                    <Option value="immediate">{t('jjcrm.timelineImmediate', 'Immediate')}</Option><Option value="within 1 month">{t('jjcrm.timeline1m', 'Within 1 month')}</Option>
                    <Option value="within 3 months">{t('jjcrm.timeline3m', 'Within 3 months')}</Option><Option value="flexible">{t('jjcrm.timelineFlexible', 'Flexible')}</Option>
                  </Select>
                </Form.Item></Col>}
              </Row>
              <Form.Item name="notes" label={t('common.notes', 'Notes')}><TextArea rows={3} /></Form.Item>
            </Form>
          </TabPane>

          {/* linked properties */}
          <TabPane tab={`🏢 ${t('jjcrm.properties', 'Properties')} (${matches.length})`} key="props">
            <Space style={{ marginBottom: 8 }}>
              <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => setLinkModal(true)}>{segment === 'home' ? t('jjcrm.linkHomeProperty', 'Link Property') : t('jjcrm.linkProperty', 'Link Office Property')}</Button>
              <Button size="small" icon={<LinkOutlined />} onClick={() => onOpenOffice && onOpenOffice()}>{segment === 'home' ? t('jjcrm.openHomeSearch', 'Open Home Search') : t('jjcrm.openOfficeSearch', 'Open Office Search')}</Button>
            </Space>
            {matches.length === 0 ? <Empty description={t('jjcrm.noLinkedProps', 'No linked properties yet')} style={{ padding: '20px 0' }} /> : (
              <List
                dataSource={matches}
                renderItem={(m) => {
                  const p = m.property || {};
                  return (
                    <Card size="small" style={{ marginBottom: 8 }} extra={
                      <Space>
                        <Tag color="green">{m.status === 'interested' ? t('jjcrm.interested', 'Interested') : m.status}</Tag>
                        <Button size="small" type="link" danger onClick={() => unlinkProperty(p.id)}>{t('common.remove', 'Remove')}</Button>
                      </Space>
                    }>
                      <div style={{ display: 'flex', gap: 10 }}>
                        {p.images && p.images[0] && <img src={p.images[0]} alt="" style={{ width: 90, height: 64, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ fontSize: 13 }}>{p.title || `Property #${p.id}`}</Text>
                          <br />
                          <Tag color={p.property_type === 'coworking' ? 'purple' : segment === 'home' ? 'blue' : 'blue'} style={{ marginRight: 6, fontSize: 10 }}>
                            {segment === 'home'
                              ? (p.property_type || t('jjcrm.home', 'Home'))
                              : (p.property_type === 'coworking' ? t('jjcrm.officeTypeCoworking', 'Coworking') : t('jjcrm.officeTypeOffice', 'Office'))}
                          </Tag>
                          <Tag color="green" style={{ fontSize: 10 }}>{fmtMoney(p.price)}{segment !== 'home' && p.property_type === 'coworking' ? ' /sqm·' + t('jjcrm.perMonth', 'month') : ' /' + t('jjcrm.perMonth', 'month')}</Tag>
                          {segment !== 'home' && p.area_ping && <Tag style={{ fontSize: 10 }}>{p.area_ping} {t('jjcrm.ping', 'ping')}</Tag>}
                          {segment !== 'home' && p.price_per_ping && <Tag style={{ fontSize: 10 }}>{t('jjcrm.perPing', 'per ping')} {fmtMoney(p.price_per_ping)}</Tag>}
                          {segment === 'home' && p.area_sqm && <Tag style={{ fontSize: 10 }}>{p.area_sqm} sqm</Tag>}
                          <br />
                          <Space size={4} style={{ fontSize: 11, color: '#595959' }}>
                            <EnvironmentOutlined /> {p.district}
                            <Badge count={m.match_score || 0} showZero color="blue" style={{ marginLeft: 8 }} />
                            {p.source_url && <Button type="link" size="small" icon={<GlobalOutlined />} href={p.source_url} target="_blank" style={{ padding: 0, fontSize: 11 }}>{t('jjcrm.sourceWebsite', 'Source Website')}</Button>}
                          </Space>
                          {m.match_reasons && m.match_reasons.length > 0 && (
                            <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>💡 {m.match_reasons.join(' · ')}</Text>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                }}
              />
            )}
          </TabPane>

          {/* negotiation */}
          <TabPane tab={`🤝 ${t('jjcrm.negotiation', 'Negotiation')} (${negotiations.length})`} key="neg">
            <Space style={{ marginBottom: 8 }}>
              <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => setNegModal(true)}>{t('jjcrm.createNegotiation', 'Create Negotiation')}</Button>
            </Space>
            {negotiations.length === 0 ? <Empty description={t('jjcrm.noNegotiations', 'No negotiations yet')} style={{ padding: '20px 0' }} /> : (
              negotiations.map(n => (
                <Card size="small" key={n.id} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="blue">{t('jjcrm.askingPrice', 'Asking')} {fmtMoney(n.asking_price)}</Tag>
                    {n.offer_price && <Tag color="orange">{t('jjcrm.offerPrice', 'Offer')} {fmtMoney(n.offer_price)}</Tag>}
                    {n.final_price && <Tag color="green">{t('jjcrm.dealPrice', 'Deal')} {fmtMoney(n.final_price)}</Tag>}
                    <Tag>{n.status === 'agreed' ? '✅ ' + t('jjcrm.negotiationAgreed', 'Agreed') : n.status === 'buyer_offered' ? '📝 ' + t('jjcrm.negotiationOfferMade', 'Offer made') : n.status === 'started' ? '🆕 ' + t('jjcrm.negotiationInProgress', 'In progress') : n.status}</Tag>
                  </Space>
                  {n.messages && (n.messages || []).map((msg, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
                      {msg.type === 'ai_suggestion' ? '🤖 ' : msg.type === 'buyer_offer' ? '📝 ' : ''}{msg.text}
                    </div>
                  ))}
                  <Space style={{ marginTop: 8 }}>
                    {!n.final_price && (
                      <Button size="small" type="primary" onClick={async () => {
                        const price = Number(n.offer_price || n.asking_price);
                        await fetch(`${API}/negotiations/${n.id}/agree?final_price=${price}`, { method: 'POST', headers: h() });
                        message.success(t('jjcrm.negotiationAgreedWon', 'Negotiation agreed and marked as won'));
                        load(); onChanged();
                      }}>{t('jjcrm.agreeClose', 'Agree & Close')}</Button>
                    )}
                  </Space>
                </Card>
              ))
            )}
          </TabPane>

          {/* activity log */}
          <TabPane tab={`📅 ${t('jjcrm.activity', 'Activity')} (${activities.length})`} key="activity">
            <Space.Compact style={{ marginBottom: 12, width: '100%' }}>
              <Input value={activityText} onChange={e => setActivityText(e.target.value)} placeholder={t('jjcrm.activityPlaceholder', 'Log activity, e.g. client visit, viewing done...')} onPressEnter={() => addActivity(activityText)} />
              <Button type="primary" onClick={() => addActivity(activityText)}>{t('jjcrm.log', 'Log')}</Button>
            </Space.Compact>
            {activities.length === 0 ? <Empty description={t('jjcrm.noActivity', 'No activity yet')} style={{ padding: '20px 0' }} /> : (
              <Timeline style={{ marginTop: 8 }}>
                {(activities || []).map(a => (
                  <Timeline.Item key={a.id} color={a.activity_type === 'contract_signed' ? 'green' : a.activity_type === 'note' ? 'blue' : 'purple'}>
                    <Text style={{ fontSize: 13 }}>{a.summary}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {a.ai_generated ? '🤖 AI' : SOURCES[a.channel]?.icon || ''} {SOURCES[a.channel]?.label || a.activity_type} ·
                      {a.created_at ? dayjs(a.created_at).format('MM/DD HH:mm') : ''}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </TabPane>
        </Tabs>
      )}

      {/* ── Link property modal ── */}
      <Modal title={segment === 'home' ? t('jjcrm.linkHomeProperty', 'Link Property') : t('jjcrm.linkProperty', 'Link Office Property')} open={linkModal} onCancel={() => setLinkModal(false)} footer={null} width={640}>
        <Input prefix={<SearchOutlined />} value={linkSearch} onChange={e => { setLinkSearch(e.target.value); searchOffice(e.target.value); }} placeholder={t('jjcrm.searchPropertyPlaceholder', 'Search property / building')} allowClear style={{ marginBottom: 12 }} />
        <List
          size="small"
          dataSource={linkResults}
          style={{ maxHeight: 360, overflow: 'auto' }}
          renderItem={(p) => (
            <List.Item
              actions={[
                <Button size="small" type="link" onClick={() => { linkProperty(p.id); }}>{t('common.add', 'Add')}</Button>,
              ]}
            >
              <Space>
                <Tag color={p.property_type === 'coworking' ? 'purple' : 'blue'} style={{ fontSize: 10 }}>
                  {segment === 'home'
                    ? (p.property_type || t('jjcrm.home', 'Home'))
                    : (p.property_type === 'coworking' ? t('jjcrm.officeTypeCoworking', 'Coworking') : t('jjcrm.officeTypeOffice', 'Office'))}
                </Tag>
                <Text style={{ fontSize: 12 }}>{p.title}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{p.district} · {fmtMoney(p.price)}/{t('jjcrm.perMonth', 'mo')}</Text>
              </Space>
            </List.Item>
          )}
        />
      </Modal>

      {/* ── Negotiation modal ── */}
      <Modal title={t('jjcrm.createNegotiation', 'Create Negotiation')} open={negModal} onCancel={() => setNegModal(false)} onOk={startNegotiation} okText={t('common.create', 'Create')} cancelText={t('common.cancel', 'Cancel')}>
        <Form form={negForm} layout="vertical" size="small">
          <Form.Item name="property_id" label={t('jjcrm.property', 'Property')} rules={[{ required: true }]}>
            <Select placeholder={t('jjcrm.selectProperty', 'Select property')} showSearch optionFilterProp="children">
              {(matches || []).map(m => (
                <Option key={m.property.id} value={m.property.id}>{m.property.title}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="asking_price" label={t('jjcrm.askingPriceField', 'Asking Price (THB/month)')} rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="offer_price" label={t('jjcrm.customerOffer', 'Customer Offer (THB/month)')}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Close deal modal ── */}
      <Modal title={closeForm && t('jjcrm.closeDealTitle', 'Close Deal / Lost')} open={closeModal} onCancel={() => setCloseModal(false)} onOk={closeDeal} okText={t('common.confirm', 'Confirm')} cancelText={t('common.cancel', 'Cancel')}>
        <Form form={closeForm} layout="vertical" size="small">
          <Form.Item name="outcome" label={t('jjcrm.outcome', 'Outcome')} rules={[{ required: true }]}>
            <Radio.Group buttonStyle="solid">
              <Radio.Button value="won">🏆 {t('jjcrm.outcomeWon', 'Won')}</Radio.Button>
              <Radio.Button value="lost">❌ {t('jjcrm.outcomeLost', 'Lost')}</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.outcome !== b.outcome}>
            {({ getFieldValue }) => getFieldValue('outcome') === 'won' ? (
              <>
                <Form.Item name="won_rent" label={t('jjcrm.wonRent', 'Won Monthly Rent (THB)')}>
                  <Input type="number" />
                </Form.Item>
                <Form.Item name="won_property_id" label={t('jjcrm.wonProperty', 'Won Property')}>
                  <Select placeholder={t('jjcrm.selectWonProperty', 'Select won property')} allowClear>
                    {(matches || []).map(m => <Option key={m.property.id} value={m.property.id}>{m.property.title}</Option>)}
                  </Select>
                </Form.Item>
              </>
            ) : (
              <Form.Item name="lost_reason" label={t('jjcrm.lostReason', 'Lost Reason')}>
                <TextArea rows={2} placeholder={t('jjcrm.lostReasonPlaceholder', 'e.g. budget mismatch / chose another vendor')} />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}

// ── Add Case Modal ──────────────────────────────────────
function AddCaseModal({ open, onClose, onAdd, apiBase, segment }) {
  const { t } = useTranslation();
  const API = apiBase || OFFICE_API;
  const PIPELINE = getPipeline(t);
  const SOURCES = getSources(t);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const vals = await form.validateFields();
    setLoading(true);
    try {
      const body = {
        ...vals,
        budget_min: vals.budget_min ? Number(vals.budget_min) : null,
        budget_max: vals.budget_max ? Number(vals.budget_max) : null,
        preferred_locations: vals.preferred_locations ? vals.preferred_locations.split(',').map(s => s.trim()).filter(Boolean) : [],
        preferred_types: vals.preferred_types ? vals.preferred_types.split(',').map(s => s.trim()).filter(Boolean) : [],
        preferred_bedrooms: vals.preferred_bedrooms ? Number(vals.preferred_bedrooms) : null,
        stage: vals.stage || 'new',
      };
      const res = await fetch(`${API}/leads`, { method: 'POST', headers: h(), body: JSON.stringify(body) }).then(r => r.json());
      if (res.id) {
        message.success(t('jjcrm.caseCreated', 'Case created'));
        form.resetFields();
        onAdd(res);
      }
    } finally { setLoading(false); }
  };

  return (
    <Modal title={segment === 'home' ? t('jjcrm.newHomeCase', 'New Home Rental Case') : t('jjcrm.newCase', 'New Office Lease Case')} open={open} onOk={handleSubmit} onCancel={() => { form.resetFields(); onClose(); }} okText={t('common.create', 'Create')} cancelText={t('common.cancel', 'Cancel')} confirmLoading={loading} width={640}>
      <Form form={form} layout="vertical" size="small" initialValues={{ stage: 'new', source: 'manual', language_pref: 'zh' }}>
        <Row gutter={12}>
          {segment !== 'home' && <Col span={12}><Form.Item name="company_name" label={t('jjcrm.companyName', 'Company Name')}><Input placeholder="e.g. Ulink" /></Form.Item></Col>}
          <Col span={segment === 'home' ? 24 : 12}><Form.Item name="name" label={t('jjcrm.contact', 'Contact')} rules={[{ required: true }]}><Input placeholder="e.g. Jeff" /></Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}><Form.Item name="phone" label={t('jjcrm.phone', 'Phone')}><Input /></Form.Item></Col>
          <Col span={8}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
          <Col span={8}><Form.Item name="source" label={t('jjcrm.sourceLabel', 'Source')}>
            <Select>
              {Object.entries(SOURCES).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
          </Form.Item></Col>
        </Row>
        {segment === 'home' ? (
        <>
        <Row gutter={12}>
          <Col span={8}><Form.Item name="preferred_types" label={t('jjcrm.preferredTypes', 'Property Types')}><Input placeholder="condo, house, townhouse" /></Form.Item></Col>
          <Col span={8}><Form.Item name="preferred_bedrooms" label={t('jjcrm.preferredBedrooms', 'Bedrooms')}><Input type="number" /></Form.Item></Col>
          <Col span={8}><Form.Item name="stage" label={t('jjcrm.stage', 'Stage')}>
            <Select>{PIPELINE.map(s => <Option key={s.key} value={s.key}>{s.label}</Option>)}</Select>
          </Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}><Form.Item name="move_in_timeline" label={t('jjcrm.moveInTimeline', 'Move-in Timeline')}>
            <Select allowClear>
              <Option value="immediate">{t('jjcrm.timelineImmediate', 'Immediate')}</Option>
              <Option value="1month">{t('jjcrm.timeline1mo', 'Within 1 month')}</Option>
              <Option value="3months">{t('jjcrm.timeline3mo', 'Within 3 months')}</Option>
              <Option value="flexible">{t('jjcrm.timelineFlexible', 'Flexible')}</Option>
            </Select>
          </Form.Item></Col>
          <Col span={12}><Form.Item name="nationality" label={t('jjcrm.nationality', 'Nationality')}><Input /></Form.Item></Col>
        </Row>
        </>
        ) : (
        <>
        <Row gutter={12}>
          <Col span={6}><Form.Item name="office_type_pref" label={t('jjcrm.officeType', 'Office Type')}>
            <Select allowClear><Option value="office">🏢 {t('jjcrm.officeTypeOffice', 'Office')}</Option><Option value="coworking">{t('jjcrm.officeTypeCoworking', 'Coworking')}</Option><Option value="serviced">{t('jjcrm.officeTypeServiced', 'Serviced')}</Option></Select>
          </Form.Item></Col>
          <Col span={6}><Form.Item name="seats_needed" label={t('jjcrm.seatsNeeded', 'Seats Needed')}><Input type="number" /></Form.Item></Col>
          <Col span={6}><Form.Item name="employee_count" label={t('jjcrm.employeeCount', 'Employees')}><Input type="number" /></Form.Item></Col>
          <Col span={6}><Form.Item name="stage" label={t('jjcrm.stage', 'Stage')}>
            <Select>{PIPELINE.map(s => <Option key={s.key} value={s.key}>{s.label}</Option>)}</Select>
          </Form.Item></Col>
        </Row>
        </>
        )}
        <Row gutter={12}>
          <Col span={segment === 'home' ? 12 : 6}><Form.Item name="budget_min" label={t('jjcrm.budgetMin', 'Budget Min (THB)')}><Input type="number" placeholder="THB" /></Form.Item></Col>
          <Col span={segment === 'home' ? 12 : 6}><Form.Item name="budget_max" label={t('jjcrm.budgetMax', 'Budget Max (THB)')}><Input type="number" placeholder="THB" /></Form.Item></Col>
          {segment !== 'home' && <Col span={6}><Form.Item name="area_sqm_max" label={t('jjcrm.areaMax', 'Max Area (sqm)')}><Input type="number" /></Form.Item></Col>}
          {segment !== 'home' && <Col span={6}><Form.Item name="lease_months" label={t('jjcrm.leaseMonths', 'Lease (months)')}><Input type="number" /></Form.Item></Col>}
        </Row>
        <Row gutter={12}>
          <Col span={segment === 'home' ? 24 : 12}><Form.Item name="preferred_locations" label={t('jjcrm.preferredLocations', 'Preferred Areas')}><Input placeholder="Pathum Wan, Sukhumvit" /></Form.Item></Col>
          {segment !== 'home' && <Col span={12}><Form.Item name="boi_status" label="BOI"><Select allowClear><Option value="none">{t('common.none', 'None')}</Option><Option value="applied">{t('jjcrm.boiApplied', 'Applied')}</Option><Option value="approved">{t('jjcrm.boiApproved', 'Approved')}</Option></Select></Form.Item></Col>}
        </Row>
        <Form.Item name="notes" label={t('jjcrm.reqNotes', 'Requirement Notes')}><TextArea rows={2} /></Form.Item>
      </Form>
    </Modal>
  );
}

// ── Main CRM Page ───────────────────────────────────────
export default function JJCRMPage() {
  const { t } = useTranslation();
  const PIPELINE = getPipeline(t);
  const SOURCES = getSources(t);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [segment, setSegment] = useState(searchParams.get('segment') === 'home' ? 'home' : 'office');
  const API = segment === 'home' ? HOME_API : OFFICE_API;

  const [leads, setLeads] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        fetch(`${API}/dashboard`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/leads?limit=200${filterStage !== 'all' ? `&stage=${filterStage}` : ''}${filterSource !== 'all' ? `&source=${filterSource}` : ''}`, { headers: h() }).then(r => r.json()),
      ]);
      setDashboard(d);
      setLeads(l.leads || []);
    } finally { setLoading(false); }
  }, [API, filterStage, filterSource]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ?focus=<lead_id> from Office Search "add to CRM" deep link
  useEffect(() => {
    const focusId = searchParams.get('focus');
    if (focusId) {
      setSelectedLeadId(Number(focusId));
      setDrawerOpen(true);
    }
  }, [searchParams]);

  const filtered = leads.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) ||
           (c.company_name || '').toLowerCase().includes(s) ||
           (c.phone || '').includes(s) ||
           (c.email || '').toLowerCase().includes(s) ||
           (c.preferred_locations || []).join(' ').toLowerCase().includes(s) ||
           (c.preferred_types || []).join(' ').toLowerCase().includes(s);
  });

  const pipeline = dashboard?.pipeline || [];

  const columns = [
    {
      title: t('jjcrm.customer', 'Customer'), key: 'client', width: 190,
      render: (_, c) => {
        const isWon = c.stage === 'closed_won';
        const isLost = c.stage === 'closed_lost';
        const primary = segment === 'home' ? c.name : (c.company_name || c.name);
        const secondary = segment === 'home' ? '' : (c.name && c.name !== c.company_name ? c.name : '');
        return (
          <Space>
            <Avatar size={32} style={{ background: isWon ? '#52c41a' : isLost ? '#ff4d4f' : '#1890ff', flexShrink: 0 }}>
              {(primary || '?')[0]}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ fontSize: 13, display: 'block' }}>{primary}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{secondary}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: t('jjcrm.requirements', 'Requirements'), key: 'req', width: 200,
      render: (_, c) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            {segment === 'home' ? (
              <>
                {(c.preferred_types || []).slice(0, 2).map(pt => <Tag color="blue" style={{ fontSize: 10, margin: 0 }} key={pt}>{pt}</Tag>)}
                {c.preferred_bedrooms && <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>🛏 {c.preferred_bedrooms}</Tag>}
                {(c.preferred_locations || []).length > 0 && <Tag color="gold" style={{ fontSize: 10, margin: 0 }}><EnvironmentOutlined /> {c.preferred_locations.slice(0, 2).join(', ')}</Tag>}
              </>
            ) : (
              <>
                {c.office_type_pref === 'coworking' && <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>🧑‍💻 {t('jjcrm.officeTypeCoworking', 'Coworking')}</Tag>}
                {c.office_type_pref === 'office' && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>🏢 {t('jjcrm.officeTypeOffice', 'Office')}</Tag>}
                {c.seats_needed && <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>👥 {c.seats_needed} {t('jjcrm.seats', 'seats')}</Tag>}
                {(c.preferred_locations || []).length > 0 && <Tag color="gold" style={{ fontSize: 10, margin: 0 }}><EnvironmentOutlined /> {c.preferred_locations.slice(0, 2).join(', ')}</Tag>}
              </>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <DollarOutlined /> {c.budget_min ? fmtMoney(c.budget_min) : ''}{c.budget_min && c.budget_max ? ' – ' : c.budget_max ? '≤ ' : ''}{c.budget_max ? fmtMoney(c.budget_max) : ''}{c.budget_min || c.budget_max ? '/' + t('jjcrm.perMonth', 'mo') : t('jjcrm.budgetNotSet', 'Budget not set')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('jjcrm.properties', 'Properties'), key: 'props', width: 90, align: 'center',
      render: (_, c) => {
        const n = (c.matches || []).length;
        return <Badge count={n} showZero color={n > 0 ? '#13c2c2' : '#d9d9d9'} />;
      },
    },
    {
      title: t('jjcrm.sourceLabel', 'Source'), key: 'source', width: 90,
      render: (_, c) => {
        const src = SOURCES[c.source] || SOURCES.manual;
        return <Tag color={src.color} style={{ fontSize: 10 }}>{src.label}</Tag>;
      },
    },
    {
      title: t('jjcrm.score', 'Score'), key: 'score', width: 80, align: 'center',
      render: (_, c) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.lead_score >= 70 ? '#ff4d4f' : c.lead_score >= 40 ? '#fa8c16' : '#595959' }}>{c.lead_score || 0}</div>
          <Progress percent={(c.lead_score || 0)} showInfo={false} size="small" strokeColor={c.lead_score >= 70 ? '#ff4d4f' : '#fa8c16'} />
        </div>
      ),
      sorter: (a, b) => (b.lead_score || 0) - (a.lead_score || 0),
    },
    {
      title: t('jjcrm.stage', 'Stage'), key: 'stage', width: 110,
      render: (_, c) => {
        return (
          <Select size="small" value={c.stage || 'new'} style={{ width: 105 }} onChange={async (val) => {
            await fetch(`${API}/leads/${c.id}`, { method: 'PATCH', headers: h(), body: JSON.stringify({ stage: val }) });
            loadAll();
          }}>
            {PIPELINE.map(st => <Option key={st.key} value={st.key}><Tag color={st.color} style={{ margin: 0, fontSize: 10 }}>{st.label}</Tag></Option>)}
          </Select>
        );
      },
    },
    {
      title: t('jjcrm.dealInfo', 'Deal Info'), key: 'won', width: 110,
      render: (_, c) => c.stage === 'closed_won'
        ? <div><Tag color="green">🏆 {fmtMoney(c.won_rent)}{c.won_rent ? '/' + t('jjcrm.perMonth', 'mo') : ''}</Tag><br /><Text type="secondary" style={{ fontSize: 10 }}>{c.won_at ? dayjs(c.won_at).format('MM/DD') : ''}</Text></div>
        : c.stage === 'closed_lost'
          ? <Tag color="red" style={{ fontSize: 10 }}>{t('jjcrm.closedLostTag', 'Lost')}</Tag>
          : <Text type="secondary" style={{ fontSize: 11 }}>-</Text>,
    },
    {
      title: t('jjcrm.lastActivity', 'Last Activity'), key: 'activity', width: 90,
      render: (_, c) => c.last_activity_at
        ? <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(c.last_activity_at).format('MM/DD HH:mm')}</Text>
        : <Text type="secondary" style={{ fontSize: 11 }}>-</Text>,
      sorter: (a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0),
    },
    {
      title: '', key: 'actions', width: 70, fixed: 'right',
      render: (_, c) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedLeadId(c.id); setDrawerOpen(true); }}>{t('common.details', 'Details')}</Button>
      ),
    },
  ];

  const statCards = [
    { title: `📋 ${t('jjcrm.totalCases', 'Total Cases')}`, value: dashboard?.total ?? 0, color: '#1890ff' },
    { title: `🔥 ${t('jjcrm.hotCases', 'Hot Cases')}`, value: dashboard?.hot ?? 0, color: '#ff4d4f' },
    { title: `👀 ${t('jjcrm.pipeline.viewing', 'Viewing')}`, value: dashboard?.viewing ?? 0, color: '#fa8c16' },
    { title: `🤝 ${t('jjcrm.pipeline.negotiating', 'Negotiating')}`, value: dashboard?.negotiating ?? 0, color: '#722ed1' },
    { title: `🏆 ${t('jjcrm.wonThisMonth', 'Won This Month')} (${dashboard?.won_month ?? 0})`, value: dashboard?.revenue_month ? '฿' + Math.round(dashboard.revenue_month).toLocaleString() : 0, color: '#52c41a' },
    { title: `📈 ${t('jjcrm.conversionRate', 'Conversion Rate')} (${dashboard?.conversion_rate ?? 0}%)`, value: dashboard?.won ?? 0, color: '#13c2c2' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{segment === 'home' ? `🏠 ${t('jjcrm.pageTitleHome', 'Bangkok Home Rental CRM')}` : `🏢 ${t('jjcrm.pageTitle', 'Bangkok Office Rental CRM')}`}</Title>
          <Text type="secondary">{segment === 'home'
            ? t('jjcrm.pageSubtitleHome', 'Residential leasing case manager — home leads, property linking, negotiation & closing')
            : t('jjcrm.pageSubtitle', 'Case Manager — customer cases, requirement tracking, property linking, negotiation & closing')}</Text>
        </div>
        <Space>
          <Select
            value={segment}
            onChange={(v) => { setSegment(v); navigate(`?segment=${v}`, { replace: true }); }}
            style={{ width: 220 }}
          >
            <Option value="office">🏢 {t('jjcrm.segmentOffice', 'Office Rental CRM')}</Option>
            <Option value="home">🏠 {t('jjcrm.segmentHome', 'Home Rental CRM')}</Option>
          </Select>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => setAddModalOpen(true)}>{t('jjcrm.newCaseBtn', 'New Case')}</Button>
          <Button icon={<ReloadOutlined />} onClick={loadAll} />
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        {statCards.map((s, i) => (
          <Col span={4} key={i}>
            <Card size="small" style={{ borderTop: `2px solid ${s.color}` }}>
              <Statistic title={<Text style={{ fontSize: 11 }}>{s.title}</Text>} value={s.value}
                valueStyle={{ fontSize: 20, color: s.color }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Pipeline bar */}
      <Card size="small" style={{ marginBottom: 12 }} title={<Text style={{ fontSize: 12 }}>{t('jjcrm.pipelineBar', 'Pipeline')}</Text>}>
        <Row gutter={8}>
          {PIPELINE.map((s, i) => {
            const p = pipeline.find(x => x.stage === s.key) || { count: 0, value: 0 };
            return (
              <Col span={4} key={s.key}>
                <div style={{ textAlign: 'center', padding: '6px 4px', borderRadius: 6, border: `1px solid ${s.color}33`, background: `${s.color}0d` }}>
                  <div style={{ fontSize: 11, color: s.color }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{p.count}</div>
                  {s.key === 'closed_won' && <Text type="secondary" style={{ fontSize: 10 }}>{p.value ? '฿' + Math.round(p.value).toLocaleString() : ''}</Text>}
                </div>
                {i < PIPELINE.length - 1 && <div style={{ textAlign: 'center', color: '#d9d9d9' }}>→</div>}
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input prefix={<SearchOutlined />} placeholder={t('jjcrm.searchPlaceholder', 'Search company / contact / phone / email / area...')} value={search} onChange={e => setSearch(e.target.value)} style={{ width: 280 }} allowClear />
          <Select value={filterStage} onChange={setFilterStage} style={{ width: 140 }} size="small">
            <Option value="all">{t('jjcrm.allStages', 'All Stages')}</Option>
            {PIPELINE.map(st => <Option key={st.key} value={st.key}><Tag color={st.color} style={{ margin: 0, fontSize: 10 }}>{st.label}</Tag></Option>)}
          </Select>
          <Select value={filterSource} onChange={setFilterSource} style={{ width: 130 }} size="small">
            <Option value="all">{t('jjcrm.allSources', 'All Sources')}</Option>
            {Object.entries(SOURCES).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
          </Select>
          <Text type="secondary" style={{ fontSize: 12 }}>{t('jjcrm.totalRecords', 'Total')} {filtered.length}</Text>
        </Space>
      </Card>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: n => `${t('jjcrm.totalRecords', 'Total')} ${n}` }}
        rowClassName={c => c.stage === 'closed_won' ? 'jj-row-won' : c.lead_score >= 70 ? 'jj-row-hot' : ''}
        onRow={c => ({ onDoubleClick: () => { setSelectedLeadId(c.id); setDrawerOpen(true); } })}
      />

      <CaseDrawer
        leadId={selectedLeadId}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedLeadId(null); }}
        onChanged={loadAll}
        onOpenOffice={() => navigate(segment === 'home' ? '/home-search' : '/office-search')}
        apiBase={API}
        segment={segment}
      />

      <AddCaseModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onAdd={() => { setAddModalOpen(false); loadAll(); }} apiBase={API} segment={segment} />

      <style>{`
        .jj-row-hot { background: #fff1f0 !important; }
        .jj-row-hot:hover td { background: #ffe7e6 !important; }
        .jj-row-won { background: #f6ffed !important; }
        .jj-row-won:hover td { background: #f0ffea !important; }
      `}</style>
    </div>
  );
}
